import * as React from 'react'
// libs
import { useLazyQuery, useMutation } from '@apollo/client/react'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DrawerModal } from 'components/UI'
// view components
import SyncRulesForm, { FilterRulesFormData } from './components/Form'
// hooks
import { useInitialValues, useModalData } from 'hooks/index'
// constants
import { Modals } from 'constants/index'
// icons
import { CloseIcon } from 'components/UI/Icons'
// constants
// graphql
import { FIELD_UPDATE_MUTATION } from 'graphql/objects/mutations'
import { BUSINESS_UNITS_SELECT_QUERY } from 'graphql/businessUnit/queries'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
import { LogicalConditionsContext } from 'containers/Risks/RiskTemplate/Edit/layers/LogicalConditions'
// types
import { IBusinessUnitsResponse, RiskTemplateFormConditionActionType } from 'types/graphql'
// utils
import { getOptionByValue } from 'utils/form'
import { parseGraphQLErrorMessages } from 'utils/errors/parsers'

type QueryResponse = { businessUnits: IBusinessUnitsResponse }

const LookupFilterModal: React.FC = () => {
  const { refetchTemplate } = React.useContext(RiskTemplateAPIContext)

  const [updateField] = useMutation(FIELD_UPDATE_MUTATION)
  const [getUnits] = useLazyQuery<QueryResponse>(BUSINESS_UNITS_SELECT_QUERY)

  const {
    hideModal,
    modalData: { isOpen, field }
  } = useModalData({ modalType: Modals.LOOKUP_FILTER_MODAL })

  const { currentCondition, onFieldFilterChange } = React.useContext(LogicalConditionsContext)

  const fieldCondition = currentCondition?.actions?.find(
    ({ fieldId, type }) => fieldId === field?.id && type === RiskTemplateFormConditionActionType.LimitResponse
  )

  const onSubmit = async (data: FilterRulesFormData) => {
    const businessUnitsIds = data.rules.map((rule) => rule.businessUnit.value)

    if (currentCondition) {
      onFieldFilterChange(field.id, businessUnitsIds)
    } else {
      try {
        await updateField({
          variables: {
            id: field.id,
            input: {
              limitResponse: {
                value: { businessUnitsIds }
              }
            }
          }
        })

        refetchTemplate()
      } catch (e) {
        console.log('[LookupFilterModal.onSubmit]: ', parseGraphQLErrorMessages(e))
      }
    }

    hideModal()
  }

  const getInitialValues = async () => {
    const businessUnitsIds = currentCondition
      ? fieldCondition?.value?.businessUnitsIds
      : field?.limitResponse?.value?.businessUnitsIds

    if (!businessUnitsIds?.length) {
      return {}
    }

    const res = await getUnits({
      variables: { filter: { ids: businessUnitsIds } }
    })

    const unitsOptions = res.data.businessUnits.data.map((unit) => ({
      label: unit.description,
      value: unit.id
    }))

    return {
      rules: businessUnitsIds.map((id: string) => ({
        property: { label: 'Business Unit', value: 'BUSINESS_UNIT' },
        action: { label: 'Is', value: 'IS' },
        businessUnit: getOptionByValue(id, unitsOptions)
      }))
    }
  }

  const initialValues = useInitialValues(getInitialValues, field, currentCondition)

  return (
    <DrawerModal width='700px' isOpen={isOpen} hideModal={hideModal}>
      <Header>
        Filter Rules
        <CloseIcon onClick={hideModal} />
      </Header>
      <SyncRulesForm onSubmit={onSubmit} onCancel={hideModal} initialValues={initialValues} />
    </DrawerModal>
  )
}

export default LookupFilterModal
